import { Clock, CheckCircle, XCircle } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Card } from "../../ui/card";
import { useSelector } from "react-redux";
import { getFeedbackForm } from "../../../redux/reducer/incbotFeedbackReducer";
import { QUESTION_TYPES } from "../../../constants";
import { getStatusV } from "../../../utils/helper";

export const CodingSection = () => {
  const feedbackForm = useSelector(getFeedbackForm);
  const codingQuestions = (feedbackForm?.questions?.data || [])?.filter(
    (question: any) => question.questionType === QUESTION_TYPES.CODING
  );
  return (
    <>
      {/* Header with background */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-sm p-6 mb-3 md:mb-6 border border-blue-100">
        <h1 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-2">Coding Questions</h1>
        <p className="text-gray-600 text-sm lg:text-base">
          Assessment of coding proficiency, logic, and problem-solving abilities.
        </p>
      </div>

      {/* Map through coding questions */}
      {codingQuestions.map((question: any, index: number) => (
        <Card key={index} className={"rounded-lg shadow-sm mb-3 md:mb-6"}>
          <div className="p-4 lg:p-6">
            <div className="flex items-start gap-3 lg:gap-4">
              <span className="text-gray-700 font-medium text-sm lg:text-base leading-relaxed">
                {index + 1}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4 gap-3">
                  <div className="flex-1 lg:pr-4">
                    <p className="text-gray-900 mb-4 text-sm lg:text-base leading-relaxed whitespace-pre-wrap">
                      {question.question}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 max-md:hidden">
                    <CheckCircle
                      className={`text-${getStatusV(question.rating).color}-500`}
                      size={18}
                    />
                    <Badge
                      variant="outline"
                      className={`text-${getStatusV(question.rating).color}-700 border-${getStatusV(question.rating).color}-200 bg-${getStatusV(question.rating).color}-50 text-xs lg:text-sm`}
                    >
                      {`${question.rating * 10}% Correct`}
                    </Badge>
                  </div>
                </div>

                {/* AI Comment */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-gray-700">Code Analysis:</span>
                    <div className="flex items-center gap-1">
                      <Clock size={14} />
                      <span className="text-xs text-gray-500">{question.timeTaken}s</span>
                    </div>
                    {question?.AITextRating &&
                      ("rating" in question.AITextRating && question.AITextRating.rating ? (
                        <Badge
                          variant="outline"
                          className="text-orange-700 border-orange-200 bg-orange-50 text-xs"
                        >
                          AI-Assisted Response
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className={`text-green-700 border-green-200 bg-green-50 text-xs ${question?.answer && question?.answer?.trim() !== "The candidate skipped the question." ? "" : "opacity-50"}`}
                        >
                          100% Human Response
                        </Badge>
                      ))}
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-gray-700">
                    {question.ratingReason || "Not Available"}
                  </div>
                </div>
                {/* Scoring Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-1">Code Quality:</div>
                    <div
                      className={`text-lg font-semibold text-${getStatusV(question.codeQuality / 10).color}-600`}
                    >
                      {question.codeQuality || 0}/10
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-1">Optimization:</div>
                    <div
                      className={`text-lg font-semibold text-${getStatusV(question.codeOptimization / 10).color}-600`}
                    >
                      {question.codeOptimization || 0}/10
                    </div>
                  </div>
                  {question.totalTestCases ? (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Test Cases:</div>
                      <div
                        className={`text-lg font-semibold text-${getStatusV(question.testCasesPassed / question.totalTestCases).color}-600`}
                      >
                        {question.testCasesPassed}/{question.totalTestCases}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">
                        Code Correctness:
                      </div>
                      <div
                        className={`text-lg font-semibold text-${getStatusV(question.codeCorrectness).color}-600`}
                      >
                        {question.codeCorrectNess || 0}/10
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-sm font-medium text-gray-700 mb-1">
                      Overall Coding Score:
                    </div>
                    <div
                      className={`text-lg font-semibold text-${getStatusV(question.codeStructure / 10).color}-600`}
                    >
                      {question.rating || 0}/10
                    </div>
                  </div>
                </div>

                {/* Code Snippet */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Code Snippet:</span>
                    <div className="text-sm font-normal text-gray-700">
                      Language: {question.language}
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-md p-4 break-words">
                    <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">
                      {question.answer || "The candidate didn't answer"}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </>
  );
};
